import type { EntityCrudPageProps } from '../../components/EntityCrudPage';

const STATE_FIELDS = [
  { key: 'state', label: 'State', type: 'text' as const, required: true, width: 0.5 as const, showInTable: true },
  { key: 'abbreviation', label: 'Abbr.', type: 'text' as const, required: true, width: 0.5 as const, showInTable: true },
];

export const entityCompliance: EntityCrudPageProps = {
  title: 'Entity Compliance',
  resourcePath: '/admin/entity-compliance',
  getRowLabel: (r) => String(r.entityKey ?? r._id),
  fields: [
    { key: 'entityKey', label: 'Entity Key', type: 'text', required: true, width: 0.5, showInTable: true, helperText: 'e.g. llc' },
    { key: 'name', label: 'Name', type: 'text', width: 0.5, showInTable: true },
    { key: 'liabilityProtection', label: 'Liability Protection', type: 'bool', width: 0.5, showInTable: true },
    { key: 'passThroughTaxation', label: 'Pass-through Taxation', type: 'bool', width: 0.5, showInTable: true },
    { key: 'annualReportRequired', label: 'Annual Report Required', type: 'bool', width: 0.5 },
    { key: 'ownersLabel', label: 'Owners Label', type: 'text', width: 0.5 },
    { key: 'federalTaxDefault', label: 'Federal Tax Default', type: 'multiline' },
    { key: 'specialNotes', label: 'Special Notes', type: 'multiline' },
  ],
};

export const stateForeignQualification: EntityCrudPageProps = {
  title: 'State Foreign Qualification',
  resourcePath: '/admin/state-foreign-qualification',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'requiredWhenDoingBusiness', label: 'Required When Doing Business', type: 'bool', width: 0.5, showInTable: true },
    { key: 'agency', label: 'Agency', type: 'text', showInTable: true },
    { key: 'formName', label: 'Form Name', type: 'text', width: 0.5 },
    { key: 'initialFee', label: 'Initial Fee', type: 'text', width: 0.5 },
    {
      key: 'renewalPeriod',
      label: 'Renewal Period',
      type: 'select',
      width: 0.5,
      showInTable: true,
      options: [
        { value: 'Annual', label: 'Annual' },
        { value: 'Biennial', label: 'Biennial' },
        { value: 'N/A', label: 'N/A' },
      ],
    },
    { key: 'onlineFilingAvailable', label: 'Online Filing Available', type: 'bool', width: 0.5 },
    { key: 'registeredAgentRequired', label: 'Registered Agent Required', type: 'bool', width: 0.5 },
    { key: 'certificateOfGoodStandingRequired', label: 'Good Standing Certificate Required', type: 'bool', width: 0.5 },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const stateAnnualReports: EntityCrudPageProps = {
  title: 'State Annual Reports',
  resourcePath: '/admin/state-annual-reports',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'required', label: 'Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'agency', label: 'Agency', type: 'text', showInTable: true },
    {
      key: 'frequency',
      label: 'Frequency',
      type: 'select',
      width: 0.5,
      showInTable: true,
      options: [
        { value: 'Annual', label: 'Annual' },
        { value: 'Biennial', label: 'Biennial' },
        { value: 'Decennial', label: 'Decennial' },
        { value: 'Quinquennial', label: 'Quinquennial' },
        { value: 'N/A', label: 'N/A' },
      ],
    },
    { key: 'fee', label: 'Fee', type: 'text', width: 0.5, showInTable: true },
    { key: 'deadline', label: 'Deadline', type: 'text', width: 0.5 },
    { key: 'deadlineRules', label: 'Deadline Rules', type: 'deadline-rules', showInTable: true },
    { key: 'onlineFilingAvailable', label: 'Online Filing Available', type: 'bool', width: 0.5 },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const stateTaxRegistrations: EntityCrudPageProps = {
  title: 'State Tax Registrations',
  resourcePath: '/admin/state-tax-registrations',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'hasStateIncomeTax', label: 'Has State Income Tax', type: 'bool', width: 0.5, showInTable: true },
    { key: 'incomeTaxAgency', label: 'Income Tax Agency', type: 'text' },
    { key: 'salesTaxPermitRequired', label: 'Sales Tax Permit Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'salesTaxAgency', label: 'Sales Tax Agency', type: 'text' },
    { key: 'employerWithholdingRegistrationRequired', label: 'Employer Withholding Registration', type: 'bool', width: 0.5 },
    { key: 'unemploymentRegistrationRequired', label: 'Unemployment Registration', type: 'bool', width: 0.5 },
    { key: 'franchiseTaxApplies', label: 'Franchise Tax Applies', type: 'bool', width: 0.5, showInTable: true },
    { key: 'franchiseTaxTitle', label: 'Franchise Tax Title', type: 'text', showInTable: true },
    { key: 'franchiseTaxFee', label: 'Franchise Tax Fee', type: 'text', width: 0.5, showInTable: true },
    { key: 'franchiseTaxDeadline', label: 'Franchise Tax Deadline', type: 'text', width: 0.5, showInTable: true },
    { key: 'franchiseTaxDeadlineRules', label: 'Franchise Tax Deadline Rules', type: 'deadline-rules', showInTable: true },
    { key: 'franchiseTaxNotes', label: 'Franchise Tax Notes', type: 'multiline' },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const stateBusinessLicenses: EntityCrudPageProps = {
  title: 'State Business Licenses',
  resourcePath: '/admin/state-business-licenses',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'generalBusinessLicenseRequired', label: 'State License Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'agency', label: 'Agency', type: 'text', showInTable: true },
    { key: 'localLicenseLikely', label: 'Local License Likely', type: 'bool', width: 0.5, showInTable: true },
    { key: 'professionalLicenseNotes', label: 'Professional License Notes', type: 'multiline' },
    { key: 'industryLicenseNotes', label: 'Industry License Notes', type: 'multiline' },
    { key: 'renewalFrequency', label: 'Renewal Frequency', type: 'text', width: 0.5 },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const stateEmploymentRequirements: EntityCrudPageProps = {
  title: 'State Employment Requirements',
  resourcePath: '/admin/state-employment-requirements',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'hasStateIncomeTax', label: 'Has State Income Tax', type: 'bool', width: 0.5, showInTable: true },
    { key: 'stateWithholdingRequired', label: 'State Withholding Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'withholdingAgency', label: 'Withholding Agency', type: 'text' },
    { key: 'stateUnemploymentRequired', label: 'State Unemployment Required', type: 'bool', width: 0.5 },
    { key: 'unemploymentAgency', label: 'Unemployment Agency', type: 'text' },
    { key: 'workersCompensationRequired', label: "Workers' Comp Required", type: 'bool', width: 0.5, showInTable: true },
    { key: 'workersCompNotes', label: "Workers' Comp Notes", type: 'multiline' },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const federalFilings: EntityCrudPageProps = {
  title: 'Federal Filings',
  resourcePath: '/admin/federal-filings',
  getRowLabel: (r) => String(r.formNumber ?? r._id),
  fields: [
    { key: 'formNumber', label: 'Form #', type: 'text', required: true, width: 0.5, showInTable: true },
    { key: 'name', label: 'Name', type: 'text', width: 0.5, showInTable: true },
    { key: 'description', label: 'Description', type: 'multiline' },
    { key: 'whoMustFile', label: 'Who Must File', type: 'multiline', showInTable: true },
    { key: 'threshold', label: 'Threshold', type: 'text', width: 0.5 },
    { key: 'deadline', label: 'Deadline', type: 'text', width: 0.5 },
    { key: 'deadlineRules', label: 'Deadline Rules', type: 'deadline-rules', showInTable: true },
    { key: 'extensionForm', label: 'Extension Form', type: 'text', width: 0.5 },
    { key: 'extensionDuration', label: 'Extension Duration', type: 'text', width: 0.5 },
    { key: 'penalty', label: 'Penalty', type: 'multiline' },
    { key: 'electronicFilingRequired', label: 'Electronic Filing Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'frequency', label: 'Frequency', type: 'text', width: 0.5 },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const questions: EntityCrudPageProps = {
  title: 'Questions',
  resourcePath: '/admin/questions',
  getRowLabel: (r) => String(r.questionId ?? r._id),
  fields: [
    { key: 'questionId', label: 'Question ID', type: 'text', required: true, width: 0.5, showInTable: true },
    { key: 'type', label: 'Type', type: 'text', required: true, width: 0.5, showInTable: true },
    { key: 'title', label: 'Title', type: 'text', showInTable: true },
    { key: 'subtitle', label: 'Subtitle', type: 'multiline' },
    { key: 'required', label: 'Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'order', label: 'Order', type: 'number', width: 0.5, showInTable: true },
    { key: 'options', label: 'Options', type: 'json', helperText: 'Array of { v, l, d } objects.' },
  ],
};

export const ENTITY_CONFIGS: Record<string, EntityCrudPageProps> = {
  'entity-compliance': entityCompliance,
  'state-foreign-qualification': stateForeignQualification,
  'state-annual-reports': stateAnnualReports,
  'state-tax-registrations': stateTaxRegistrations,
  'state-business-licenses': stateBusinessLicenses,
  'state-employment-requirements': stateEmploymentRequirements,
  'federal-filings': federalFilings,
  questions,
};
